import React from 'react';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { keyframes } from 'styled-components';
export function ReactMemo(f) {
    return React.memo((p) => {
        return f(p);
    });
}
export const fromDateTime = (date, time, isBC = false) => {
    let [day, month, year] = date.split('.');
    return `${isBC ? `-${year.padStart(6, '0')}` : year}-${month}-${day}T${time}.000Z`;
};
export const toDateTime = (dt) => {
    const isBC = dt.startsWith('-');
    try {
        const [, date, time] = dt.match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})\.\d+(Z|[-+]\d{2}:\d{2})/);
        return {
            date: `${date.split('-').reverse().join('.')}${isBC ? 'B' : ''}`,
            time
        };
    }
    catch (_a) {
        return {
            date: '01.01.2021',
            time: '00:00:00'
        };
    }
};
export function asDateTime(isoDateString) {
    const d = dayjs(isoDateString);
    if (!d.isValid())
        return {};
    return {
        date: `${d.month()}.${d.day()}.${d.year()}`,
        time: `${d.hour()}:${d.minute()}:${d.second()}`
    };
}
export function addGMTToISOString(isoDateString) {
    return dayjs(isoDateString).format('YYYY-MM-DDTHH:mm:ss.000') + 'Z';
}
export function subtractGMTToISOString(isoDateString) {
    return dayjs(isoDateString.replace('Z', '')).toISOString();
}
export function daysInMonth(month, year) {
    if (month === 2) {
        return year % 400 === 0 || year % 4 === 0 && year % 100 !== 0 ? 29 : 28;
    }
    return 30 + [1, 0, 1, 0, 1, 0, 1, 1, 0, 1, 0, 1][month - 1];
}
export const getDeviceInfo = () => {
    var _a;
    const userAgent = (typeof globalThis.navigator) === 'undefined' ? '' : (_a = globalThis.navigator) === null || _a === void 0 ? void 0 : _a.userAgent;
    const isMobile = Boolean(/android|webos|iphone|ipod|blackberry|bb|playbook|iemobile|windows phone|kindle|silk|opera mini|wpdesktop/i.exec(userAgent));
    const isTablet = Boolean(/Tablet|iPad|Nexus 7|Nexus 10|SM-T\d+|KFAPWI/i.exec(userAgent));
    return { isMobile, isTablet };
};
export function isMobile() {
    const toMatch = [
        /Android/i,
        /webOS/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i
    ];
    return toMatch.some((toMatchItem) => {
        return navigator.userAgent.match(toMatchItem);
    });
}
export const scrollbarWidth = () => {
    const scrollDiv = document.createElement('div');
    scrollDiv.setAttribute('style', 'width: 100px; height: 100px; overflow: scroll; position:absolute; top:-9999px;');
    document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);
    return scrollbarWidth;
};
export const FadeIn = keyframes `
  from {
    opacity: 0;
    transform: translateY(-30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
export const fadeFromLeft = keyframes `
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
`;
const parseKeyname = (keyname) => {
    const keys = Array.isArray(keyname) ? keyname : [keyname];
    return keys.map(key => {
        const splitted = key.split('+').map(k => k.trim());
        return {
            altKey: splitted[0].includes('Alt'),
            ctrlKey: splitted[0].includes('Control'),
            shiftKey: splitted[0].includes('Shift'),
            code: splitted[splitted.length - 1],
            default: key,
            splitted
        };
    });
};
const getDesireKey = (keys, ev) => {
    for (const key of keys) {
        const isPressed = key.altKey === ev.altKey
            && key.ctrlKey === ev.ctrlKey
            && key.shiftKey === ev.shiftKey
            && key.code === ev.code;
        if (isPressed)
            return key.default;
    }
    return '';
};
let timeStamp = 0;
export function checkActiveInput() {
    var _a, _b;
    return ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.nodeName.toUpperCase().includes('INPUT'))
        || ((_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.nodeName.toUpperCase().includes('TEXTAREA'));
}
const defaultParams = {
    execute: true,
    ignoreActiveInput: false,
    stopPropagation: false,
    repeat: true
};
export function useKeyHook(keyname, controller, dependencies = [], params = defaultParams) {
    const timeoutId = useRef(null);
    const _params = {
        ...defaultParams,
        ...params
    };
    useEffect(() => {
        const parsedKeyname = parseKeyname(keyname);
        const onKeyDown = (evt) => {
            if (!_params.ignoreActiveInput && checkActiveInput() || !_params.repeat && evt.repeat)
                return;
            const desireKey = getDesireKey(parsedKeyname, evt);
            if (!desireKey || !_params.ignoreActiveInput && checkActiveInput())
                return;
            _params.stopPropagation && evt.stopImmediatePropagation();
            _params.stopPropagation && evt.preventDefault();
            if (controller.delay && !evt.repeat)
                timeStamp = Date.now();
            controller.onKeyDown && controller.onKeyDown(desireKey);
            if (controller.delay) {
                timeoutId.current = setTimeout(() => {
                    controller.onKeyDown && controller.onKeyDown(desireKey, true);
                }, controller.delay);
            }
        };
        const onKeyUp = (evt) => {
            const desireKey = getDesireKey(parsedKeyname, evt);
            if (!desireKey || !_params.ignoreActiveInput && checkActiveInput())
                return;
            _params.stopPropagation && evt.stopImmediatePropagation();
            _params.stopPropagation && evt.preventDefault();
            const isDelayed = !controller.delay || Date.now() - timeStamp > controller.delay;
            if (!isDelayed && timeoutId.current)
                clearTimeout(timeoutId.current);
            controller.onKeyUp && controller.onKeyUp(desireKey, isDelayed);
        };
        if (!_params.execute)
            return;
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
        };
    }, [...dependencies]);
}
export const fireEvent = (e, eventName) => {
    var _a;
    let newEvent = new CustomEvent(eventName, { bubbles: true, cancelable: true, detail: {} });
    (_a = e.target) === null || _a === void 0 ? void 0 : _a.dispatchEvent(newEvent);
    return false;
};
export const clickOutsideHook = (ref, cb, deps = []) => {
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                cb();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, ...deps]);
};
